<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline'
import Dropdown from '@/Components/Dropdown.vue'
import DropdownLink from '@/Components/DropdownLink.vue'
import { router } from '@inertiajs/vue3'
import NavLink from '@/Layouts/Partials/NavLink.vue'
import ToggleMode from '@/Utils/ToggleMode.vue'

const navigation = [
    { name: 'Domů', href: 'home' },
    { name: 'Slevy', href: 'discounts.index' },
    { name: 'Blog', href: 'blogs.index' },
    { name: 'O nás', href: 'abouts.index' },
]

const logout = () => {
    router.post(route('logout'))
}
</script>

<template>
    <Disclosure
        as="nav"
        class="bg-zinc-200 dark:bg-zinc-950"
        v-slot="{ open }"
    >
        <div class="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
            <div class="relative flex h-16 items-center justify-between">
                <div class="absolute inset-y-0 right-0 flex items-center sm:hidden">
                    <!-- Mobile menu button-->
                    <DisclosureButton
                        class="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:ring-2 focus:ring-white focus:outline-none focus:ring-inset"
                    >
                        <span class="absolute -inset-0.5" />
                        <span class="sr-only">Open main menu</span>
                        <Bars3Icon
                            v-if="!open"
                            class="block size-6"
                            aria-hidden="true"
                        />
                        <XMarkIcon
                            v-else
                            class="block size-6"
                            aria-hidden="true"
                        />
                    </DisclosureButton>
                </div>
                <div class="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                    <div class="flex shrink-0 items-center">
                        <Link
                            :href="route('home')"
                            aria-label="Domů"
                            class="flex items-center"
                        >
                            <img
                                class="hidden size-14 object-contain dark:block"
                                src="/src/logo/logo-darkmode.png"
                                alt="priznani-snadno.cz"
                            />

                            <img
                                class="block size-14 object-contain dark:hidden"
                                src="/src/logo/logo-lightmode.png"
                                alt="priznani-snadno.cz"
                            />
                        </Link>
                    </div>

                    <div class="hidden w-full justify-between sm:ml-6 sm:flex">
                        <div class="flex items-center space-x-4">
                            <NavLink
                                v-for="item in navigation"
                                :key="item.name"
                                :href="route(item.href)"
                                :active="route().current(item.href)"
                                :aria-label="'stránka ' + item.name"
                                prefetch
                                :cache-for="['1m', '10m']"
                            >
                                {{ item.name }}
                            </NavLink>

                            <Link
                                :href="route('orders.create')"
                                class="rounded-md bg-sky-500 px-3 py-2 text-sm font-medium text-white ring-1 ring-sky-300 ring-inset hover:bg-sky-400"
                            >
                                Podat daňové přiznání
                            </Link>

                            <ToggleMode />
                        </div>

                        <div class="flex items-center space-x-4">
                            <div
                                v-if="$page.props.auth.user"
                                class="relative ms-3"
                            >
                                <Dropdown
                                    align="right"
                                    width="56"
                                >
                                    <template #trigger>
                                        <button
                                            v-if="$page.props.jetstream.managesProfilePhotos"
                                            class="flex rounded-full border-2 border-transparent text-sm transition focus:border-gray-300 focus:outline-none"
                                        >
                                            <img
                                                class="size-8 rounded-full object-cover"
                                                :src="$page.props.auth.user.profile_photo_url"
                                                :alt="$page.props.auth?.user?.name"
                                            />
                                        </button>

                                        <span
                                            v-else
                                            class="inline-flex rounded-md"
                                        >
                                            <button
                                                type="button"
                                                class="inline-flex items-center rounded-md border border-transparent bg-white px-3 py-2 text-sm leading-4 font-medium text-gray-500 transition duration-150 ease-in-out hover:text-gray-700 focus:bg-gray-50 focus:outline-none active:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-gray-300 dark:focus:bg-gray-700 dark:active:bg-gray-700"
                                            >
                                                {{ $page.props.auth.user.name }}

                                                <svg
                                                    class="ms-2 -me-0.5 size-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke-width="1.5"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                                    />
                                                </svg>
                                            </button>
                                        </span>
                                    </template>

                                    <template #content>
                                        <div class="block px-4 py-2 text-xs text-gray-400">Daňové přiznání</div>

                                        <DropdownLink :href="route('orders.create')"> Podat daňové přiznání </DropdownLink>

                                        <DropdownLink :href="route('orders.history')"> Historie daňových přiznání </DropdownLink>

                                        <div class="border-t border-gray-600" />

                                        <div class="block px-4 py-2 text-xs text-gray-400">Správa účtu</div>

                                        <DropdownLink :href="route('profile.show')"> Profil </DropdownLink>

                                        <DropdownLink
                                            as="a"
                                            :href="route('filament.dashboard.home')"
                                            v-if="$page.props.auth.user.is_admin"
                                        >
                                            Dashboard
                                        </DropdownLink>

                                        <!-- Authentication -->
                                        <form @submit.prevent="logout">
                                            <DropdownLink as="button"> Odhlásit se </DropdownLink>
                                        </form>
                                    </template>
                                </Dropdown>
                            </div>

                            <Link
                                v-else
                                :href="route('login')"
                                class="px rounded-md bg-sky-500 px-3 py-2 text-sm font-medium text-white hover:bg-zinc-900 hover:text-white"
                            >
                                Přihlásit se
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <DisclosurePanel class="sm:hidden">
            <div class="space-y-1 px-2 pt-2 pb-3">
                <Link
                    v-for="item in navigation"
                    :key="item.name"
                    :href="route(item.href)"
                    :class="[
                        route().current(item.href)
                            ? 'bg-zinc-800 text-white'
                            : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-700 hover:text-white',
                        'block rounded-md px-3 py-4 text-base font-medium',
                    ]"
                    :arial-label="'stránka ' + item.name"
                >
                    {{ item.name }}
                </Link>

                <Link
                    :href="route('orders.create')"
                    arial-label="Podat daňové přiznání"
                    class="block rounded-md bg-sky-500 px-3 py-4 text-base font-medium text-white ring-1 ring-sky-300 ring-inset hover:bg-sky-400"
                >
                    Podat daňové přiznání
                </Link>
            </div>

            <div class="border-y border-zinc-700 py-4">
                <ToggleMode />
            </div>

            <div class="space-y-1 px-2 pt-2 pb-3">
                <template v-if="!$page.props.auth.user">
                    <Link
                        :href="route('login')"
                        arial-label="Podat daňové přiznání"
                        :class="[
                            route().current('login')
                                ? 'bg-zinc-800 text-white'
                                : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-700 hover:text-white',
                            'block rounded-md px-3 py-4 text-base font-medium',
                        ]"
                    >
                        Přihlásit se
                    </Link>
                </template>

                <template v-else>
                    <Link
                        :href="route('orders.history')"
                        arial-label="Podat daňové přiznání"
                        :class="[
                            route().current('orders.history')
                                ? 'bg-zinc-800 text-white'
                                : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-700 hover:text-white',
                            'block rounded-md px-3 py-4 text-base font-medium',
                        ]"
                    >
                        Historie daňových přiznání
                    </Link>

                    <Link
                        :href="route('profile.show')"
                        arial-label="Podat daňové přiznání"
                        :class="[
                            route().current('profile.show')
                                ? 'bg-zinc-800 text-white'
                                : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-700 hover:text-white',
                            'block rounded-md px-3 py-4 text-base font-medium',
                        ]"
                    >
                        Profil
                    </Link>

                    <form @submit.prevent="logout">
                        <button
                            type="submit"
                            arial-label="Odhlásit se"
                            class="block w-full rounded-md bg-zinc-900 px-3 py-4 text-left text-base font-medium text-zinc-300 hover:bg-zinc-700 hover:text-white"
                        >
                            Odhlásit se
                        </button>
                    </form>
                </template>
            </div>
        </DisclosurePanel>
    </Disclosure>
</template>
